import { SxProps, Theme } from "@mui/material";

// Global keyframes for live text blinking animation
if (typeof document !== "undefined") {
  const styleId = "live-dot-blink-animation";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes liveDotBlink {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.3;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Top bar
export const topBarLeftGroupSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 0.5, sm: 1 },
  minWidth: 0,
  flex: { xs: 1, sm: "none" },
};

export const simulationTitleLabelSx: SxProps<Theme> = {
  fontWeight: 800,
  letterSpacing: 0.6,
  fontSize: { xs: 10, sm: 12 },
};

export const simulationTitleValueSx: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: 10, sm: 12 },
  opacity: 0.95,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: { xs: 100, sm: 240, md: 520 },
};

export const livePillSx: SxProps<Theme> = {
  ml: { xs: 0.5, sm: 1 },
  display: "inline-flex",
  alignItems: "center",
  gap: { xs: 0.25, sm: 0.5 },
  px: { xs: 0.5, sm: 0.75 },
  py: { xs: 0.1, sm: 0.2 },
  borderRadius: "999px",
  backgroundColor: "rgba(46, 204, 113, 0.16)",
  border: "1px solid rgba(46, 204, 113, 0.35)",
};

export const liveDotSx: SxProps<Theme> = {
  width: { xs: 5, sm: 7 },
  height: { xs: 5, sm: 7 },
  borderRadius: "50%",
  backgroundColor: "rgba(46, 204, 113, 1)",
  animation: "liveDotBlink 1.5s ease-in-out infinite",
};

export const liveTextSx: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 9, sm: 11 },
  fontWeight: 800,
  color: theme.palette.mode === "dark" ? "#B7F5C8" : "#15803D",
});

// Left panel: Context
export const contextInnerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  mt: { xs: 0.5, sm: 1, md: 1 },
  flex: "1 1 auto",
  justifyContent: "center",
};

export const doctorAvatarSx: SxProps<Theme> = {
  width: {
    xs: "min(100%, 100px)",
    sm: "min(100%, 140px)",
    md: "min(100%, 170px)",
  },
  aspectRatio: "1 / 1",
  height: "auto",
  maxHeight: {
    xs: "min(100px, 12vh)",
    sm: "min(140px, 18vh)",
    md: "min(170px, 22vh)",
  },
  borderRadius: "14px",
  objectFit: "cover",
  objectPosition: "top center",
  display: "block",
  // Mobile landscape only (rotate on phones) — do NOT shrink on desktop landscape
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      width: "60px",
      maxHeight: "60px",
    },
};

export const doctorNameSx: SxProps<Theme> = {
  mt: { xs: 0.25, sm: 0.5, md: 0.5 },
  fontWeight: 900,
  letterSpacing: 0.4,
  fontSize: { xs: 12, sm: 14, md: 14 },
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      fontSize: "10px",
      mt: 0.125,
    },
};

export const doctorLevelSx: SxProps<Theme> = {
  opacity: 0.75,
  fontSize: { xs: 9, sm: 11 },
  fontWeight: 600,
  mt: { xs: 0.1, sm: 0.25 },
  mb: { xs: 0.25, sm: 0.5, md: 0.5 },
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      fontSize: "8px",
      mt: 0.05,
    },
};

export const sectionTitleSx: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 11, sm: 12, md: 12 },
  fontWeight: 700,
  mb: { xs: 0.5, sm: 0.75, md: 0.75 },
  color: theme.palette.text.secondary,
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      fontSize: "9px",
      mb: 0.25,
    },
});

export const sectionBodySx: SxProps<Theme> = (theme) => ({
  mt: { xs: 0, sm: 0.75, md: 0.75 },
  fontSize: { xs: 11, sm: 12, md: 12 },
  lineHeight: { xs: 1.4, sm: 1.5, md: 1.5 },
  flex: "1 1 auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: { xs: "flex-start", sm: "center", md: "center" },
  whiteSpace: "normal",
  color: theme.palette.text.primary,
  "& strong": {
    display: "inline",
    fontWeight: 700,
  },
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      fontSize: "9px",
      mt: 0.25,
      lineHeight: 1.3,
    },
});

export const sectionBodyInlineSx: SxProps<Theme> = {
  mt: { xs: 0.5, sm: 0.75, md: 0.75 },
  fontSize: { xs: 11, sm: 12, md: 12 },
  opacity: 0.9,
  lineHeight: { xs: 1.4, sm: 1.5, md: 1.5 },
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "& strong": {
    display: "inline",
    fontWeight: 700,
  },
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      fontSize: "9px",
      mt: 0.25,
      lineHeight: 1.3,
    },
};

export const bottomAlignedCardSx: SxProps<Theme> = {
  mt: { md: "auto" },
};

export const fillRemainingCardSx: SxProps<Theme> = {
  flexGrow: { md: 1 },
  display: { md: "flex" },
  flexDirection: { md: "column" },
  justifyContent: { md: "center" },
  minHeight: { md: 0 },
};

// Mobile/Tablet: stack all blocks vertically, with THE STAGE last and filling remaining height.
export const mobileStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 0.5, sm: 0.75, md: 0.75 },
  height: "100%",
  minHeight: 0,
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      flexDirection: "row",
      gap: "4px",
    },
  "@media (orientation: landscape) and (max-height: 500px)": {
    height:
      "calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
  },
};

export const mobileCardsSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 0.75, sm: 1, md: 1 },
  flex: "1 1 0%",
  minHeight: 0,
  overflow: "hidden",
  justifyContent: "flex-start",
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      flex: "0 0 40%",
      gap: "2px",
      overflow: "hidden",
      width: "40%",
      maxWidth: "40%",
      height: "100%",
    },
};

export const mobileStageSx: SxProps<Theme> = {
  flex: "0 0 auto",
  minHeight: 0,
  maxHeight: {
    xs: "calc(50dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
    sm: "45dvh",
    md: "45vh",
  },
  overflow: "hidden",
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      flex: "1 1 60%",
      maxHeight: "none",
      minHeight: 0,
      width: "60%",
      maxWidth: "60%",
      height: "100%",
    },
  // For larger iPhones (14 Pro Max and newer) in portrait
  "@media (min-width: 430px) and (max-width: 900px) and (orientation: portrait)":
    {
      maxHeight:
        "calc(50dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 60px)",
    },
};

export const mobileCardCompactSx = {
  px: { xs: 1, sm: 1.5, md: 1.5 },
  py: { xs: 1, sm: 1.5, md: 1.5 },
  flex: "1 1 auto",
  display: "flex",
  flexDirection: "column",
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      px: 0.5,
      py: 0.25,
      flex: "0 0 auto",
    },
};

// Stage panel
export const stagePanelContainerSx = {
  p: 0,
  gap: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  height: "100%",
};

export const stageHeaderSx: SxProps<Theme> = {
  px: { xs: 0.75, sm: 1.5, md: 1.5 },
  pt: { xs: 0.75, sm: 1.5, md: 1.5 },
  pb: { xs: 0.25, sm: 0.5, md: 0.5 },
  flexShrink: 0,
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      px: 0.5,
      pt: 0.25,
      pb: 0.125,
    },
};

export const stageHeaderTitleSx: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 10, sm: 11, md: 11 },
  fontWeight: 800,
  color: theme.palette.text.secondary,
});

export const stageAreaSx: SxProps<Theme> = {
  position: "relative",
  flex: 1,
  minHeight: 0,
  px: { xs: 0.75, sm: 1.5, md: 1.5 },
  pb: {
    xs: "max(8px, calc(0.5rem + env(safe-area-inset-bottom, 0px)))",
    sm: 1,
    md: 1,
  },
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      px: 0.5,
      pb: "max(4px, calc(0.25rem + env(safe-area-inset-bottom, 0px)))",
    },
};

export const waveLabelSx: SxProps<Theme> = {
  fontSize: { xs: 10, sm: 11, md: 11 },
  fontWeight: 800,
  opacity: 0.9,
  textAlign: "center",
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      fontSize: "8px",
    },
};

export const waveLabelBottomSx: SxProps<Theme> = {
  ...waveLabelSx,
  mb: { xs: 0.25, sm: 0.5, md: 0.5 },
  flexShrink: 0,
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
    {
      mb: 0.125,
    },
};

export const waveStretchSx: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  alignItems: "stretch",
};

export const centerControlsSx: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: { xs: 0.5, sm: 0.75 },
  zIndex: 5,
  pointerEvents: "auto",
};

export const roleplayCenterControlsSx: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: { xs: 10, sm: 16, md: 24 },
  right: { xs: 10, sm: 16, md: 24 },
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 5,
  pointerEvents: "auto",
};

export const muteToggleLabelSx: SxProps<Theme> = {
  mb: { xs: 0.25, sm: 0.5, md: 0.5 },
  mt: { xs: 0.25, sm: 0.5, md: 0.5 },
  fontSize: { xs: 9, sm: 11 },
  opacity: 0.8,
  fontWeight: 700,
};

export const endButtonSx: SxProps<Theme> = (theme) => ({
  mt: { xs: 0.25, sm: 0.5 },
  width: { xs: 32, sm: 40 },
  height: { xs: 32, sm: 40 },
  borderRadius: "999px",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(231, 76, 60, 0.18)"
      : "rgba(220, 38, 38, 0.12)",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(231, 76, 60, 0.35)"
      : "rgba(185, 28, 28, 0.45)"
  }`,
  color: theme.palette.mode === "dark" ? undefined : "#B91C1C",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(231, 76, 60, 0.24)"
        : "rgba(220, 38, 38, 0.18)",
  },
});

export const topHalfSx = (
  centerClearHalfPx: number,
  sidePadPx: number = 16,
): SxProps<Theme> => ({
  position: "absolute",
  top: 0,
  left: sidePadPx,
  right: sidePadPx,
  bottom: `calc(50% + ${centerClearHalfPx}px)`,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  gap: 1,
  zIndex: 1,
  pointerEvents: "none",
});

export const bottomHalfSx = (
  centerClearHalfPx: number,
  sidePadPx: number = 16,
): SxProps<Theme> => ({
  position: "absolute",
  bottom: 0,
  left: sidePadPx,
  right: sidePadPx,
  top: `calc(50% + ${centerClearHalfPx}px)`,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  gap: 1,
  zIndex: 1,
  pointerEvents: "none",
});
